import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { SNAKE_CONFIG } from './snakeConfig.js';

const { COLOR_FOOD } = SNAKE_CONFIG;
const INSET = 4;

/**
 * Food item for the snake game.
 * Spawns at a random empty cell, renders with glow and shine.
 */
export class SnakeFood {
    col = 0;
    row = 0;
    graphic = new Container();
    _dirty = true;
    _lastOx = 0;
    _lastOy = 0;
    _lastCs = 0;
    _themeManager = null;

    constructor(themeManager = null) {
        this._themeManager = themeManager;
    }

    /**
     * @param {number} cols
     * @param {number} rows
     * @param {import('./SnakeBody.js').SnakeBody} snake
     * @param {import('./SnakeObstacles.js').SnakeObstacles} [obstacles]
     * @param {import('./SnakePerks.js').SnakePerks} [perks]
     */
    spawn(cols, rows, snake, obstacles = null, perks = null) {
        const emptyCells = [];
        for (let c = 0; c < cols; c++) {
            for (let r = 0; r < rows; r++) {
                if (!snake.occupies(c, r)
                    && !(obstacles && obstacles.occupies(c, r))
                    && !(perks && perks.occupies(c, r))) {
                    emptyCells.push({ col: c, row: r });
                }
            }
        }
        if (emptyCells.length === 0) return;
        const pick = emptyCells[Math.floor(Math.random() * emptyCells.length)];
        this.col = pick.col;
        this.row = pick.row;
        this._dirty = true;
    }

    /**
     * @param {number} originX
     * @param {number} originY
     * @param {number} cellSize
     */
    render(originX, originY, cellSize) {
        if (!this._dirty && originX === this._lastOx && originY === this._lastOy && cellSize === this._lastCs) {
            return;
        }
        this._dirty = false;
        this._lastOx = originX;
        this._lastOy = originY;
        this._lastCs = cellSize;

        const cx = originX + this.col * cellSize + cellSize / 2;
        const cy = originY + this.row * cellSize + cellSize / 2;
        const radius = (cellSize - INSET * 2) / 2;
        this.graphic.removeChildren();

        const sprite = this._themeManager?.createSprite('snake.food', cellSize - INSET * 2, cellSize - INSET * 2, {
            fitMode: 'contain',
        });
        if (sprite) {
            sprite.x = cx;
            sprite.y = cy;
            this.graphic.addChild(sprite);
            return;
        }

        const g = new Graphics();

        g.circle(cx, cy, radius + 5);
        g.fill({ color: COLOR_FOOD, alpha: 0.15 });
        g.circle(cx, cy, radius + 2);
        g.fill({ color: COLOR_FOOD, alpha: 0.25 });

        g.circle(cx, cy, radius);
        g.fill(COLOR_FOOD);

        g.circle(cx, cy, radius * 0.7);
        g.fill({ color: 0xfde68a, alpha: 0.45 });

        g.circle(cx - radius * 0.25, cy - radius * 0.25, radius * 0.3);
        g.fill({ color: 0xfffbeb, alpha: 0.6 });
        this.graphic.addChild(g);
    }

    /**
     * Viewport culling — hide food if off-screen.
     */
    cull(vpX, vpY, vpW, vpH, cellSize) {
        const fx = this._lastOx + this.col * cellSize;
        const fy = this._lastOy + this.row * cellSize;
        this.graphic.visible =
            fx + cellSize > vpX && fx < vpX + vpW &&
            fy + cellSize > vpY && fy < vpY + vpH;
    }

    destroy() {
        this.graphic.removeChildren();
        if (this.graphic.parent) this.graphic.parent.removeChild(this.graphic);
    }
}
